import { Button } from '@/client/components/ui/button';
import { useTheme } from '@/client/hooks/useTheme';
import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronRight, Menu, Moon, Search, Sun } from 'lucide-react';
import { useState } from 'react';
import { CommandMenu } from './command-menu';
import { MobileSidebar } from './sidebar';

const TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/people': 'People',
  '/projects': 'Projects',
  '/timesheets': 'My Timesheets',
  '/timesheets/admin': 'All Timesheets',
  '/timesheets/shadow': 'Shadow Timesheets',
  '/finance': 'Finance',
  '/finance/new': 'New record',
  '/invoices': 'Invoices',
  '/invoices/new': 'New invoice',
  '/analytics': 'Analytics',
  '/settings': 'Settings',
  '/timeoff': 'Time Off',
};

const NAV_ROOTS = new Set([
  '/',
  '/people',
  '/projects',
  '/timesheets',
  '/timesheets/admin',
  '/timesheets/shadow',
  '/finance',
  '/invoices',
  '/analytics',
  '/settings',
  '/timeoff',
]);

/** Builds a breadcrumb trail like Invoices › INV-0042 from the current path. */
function useCrumbs() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Top-level nav destinations are their own root — no parent trail.
  if (NAV_ROOTS.has(pathname)) return [{ to: pathname, label: TITLES[pathname] }];
  const parts = pathname.split('/').filter(Boolean);
  const crumbs: { to: string; label: string }[] = [];
  let acc = '';
  for (const part of parts) {
    acc += `/${part}`;
    const known = TITLES[acc];
    if (known) crumbs.push({ to: acc, label: known });
    else if (crumbs.length > 0) crumbs.push({ to: acc, label: 'Details' });
  }
  return crumbs.length ? crumbs : [{ to: pathname, label: 'Gara' }];
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

export function TopBar() {
  const crumbs = useCrumbs();
  const { isDark, toggleTheme } = useTheme();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="surface-glass sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b px-3 sm:gap-3 sm:px-6">
      <Button
        variant="ghost"
        size="icon"
        type="button"
        onClick={() => setMenuOpen(true)}
        aria-label="Open menu"
        className="-ml-1 shrink-0 lg:hidden"
      >
        <Menu className="h-5 w-5" />
      </Button>
      <nav aria-label="Breadcrumb" className="flex min-w-0 flex-1 items-center gap-1.5 text-sm">
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          return (
            <span
              key={c.to}
              className={
                i < crumbs.length - 1
                  ? 'hidden min-w-0 items-center gap-1.5 sm:flex'
                  : 'flex min-w-0 items-center gap-1.5'
              }
            >
              {i > 0 && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />}
              {last ? (
                <span className="truncate font-semibold tracking-tight">{c.label}</span>
              ) : (
                <Link
                  to={c.to}
                  className="truncate text-muted-foreground transition-colors hover:text-foreground"
                >
                  {c.label}
                </Link>
              )}
            </span>
          );
        })}
      </nav>

      <button
        type="button"
        onClick={() => setPaletteOpen(true)}
        aria-label="Search or jump to"
        className="group flex h-9 w-9 shrink-0 items-center justify-center gap-2 rounded-lg border bg-card text-sm text-muted-foreground shadow-xs transition-all hover:border-signal/40 hover:text-foreground md:w-64 md:justify-start md:px-3"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="hidden flex-1 text-left md:inline">Search or jump to…</span>
        <kbd className="pointer-events-none hidden h-5 select-none items-center md:inline-flex gap-0.5 rounded border bg-muted px-1.5 font-sans text-[10.5px] font-medium">
          {isMac ? '⌘' : 'Ctrl'}K
        </kbd>
      </button>

      <Button
        variant="ghost"
        size="icon"
        type="button"
        onClick={toggleTheme}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        className="text-muted-foreground hover:text-foreground"
      >
        {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </Button>

      <CommandMenu open={paletteOpen} onOpenChange={setPaletteOpen} />
      <MobileSidebar open={menuOpen} onOpenChange={setMenuOpen} />
    </header>
  );
}
